import React, { useState, useEffect } from 'react';

const TicTacToe = () => {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [xIsNext, setXIsNext] = useState(true);
  const [winner, setWinner] = useState(null);
  const [gameMode, setGameMode] = useState('vsComputer'); // 'vsComputer' or 'twoPlayer'
  const [difficulty, setDifficulty] = useState('medium'); // 'easy', 'medium', or 'hard'
  const [score, setScore] = useState({ x: 0, o: 0, ties: 0 });
  const [gameHistory, setGameHistory] = useState([]);
  const [currentMove, setCurrentMove] = useState(0);
  const [isReviewing, setIsReviewing] = useState(false);
  const [isComputerThinking, setIsComputerThinking] = useState(false);
  
  // Load saved game state from localStorage
  useEffect(() => {
    try {
      const savedScore = JSON.parse(localStorage.getItem('webos-tictactoe-score'));
      if (savedScore) {
        setScore(savedScore);
      }
    } catch (e) {
      console.error('Failed to load game score:', e);
    }
  }, []);
  
  // Calculate winner
  const calculateWinner = (squares) => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8], // rows
      [0, 3, 6], [1, 4, 7], [2, 5, 8], // columns
      [0, 4, 8], [2, 4, 6]             // diagonals
    ];
    
    for (let i = 0; i < lines.length; i++) {
      const [a, b, c] = lines[i];
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return { winner: squares[a], line: lines[i] };
      }
    }
    
    // Check for draw
    if (squares.every(square => square !== null)) {
      return { winner: 'draw', line: [] };
    }
    
    return null;
  };
  
  // Check for winner after each move
  useEffect(() => {
    const result = calculateWinner(board);
    if (result) {
      setWinner(result);
      
      // Update score
      if (result.winner === 'X') {
        setScore(prev => ({ ...prev, x: prev.x + 1 }));
      } else if (result.winner === 'O') {
        setScore(prev => ({ ...prev, o: prev.o + 1 }));
      } else if (result.winner === 'draw') {
        setScore(prev => ({ ...prev, ties: prev.ties + 1 }));
      }
    }
  }, [board]);
  
  // Save score to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('webos-tictactoe-score', JSON.stringify(score));
  }, [score]);
  
  // Computer move logic
  useEffect(() => {
    if (gameMode === 'vsComputer' && !xIsNext && !winner && !isReviewing) {
      setIsComputerThinking(true);
      const delay = Math.random() * 800 + 400; // Random delay between 400-1200ms
      
      const timer = setTimeout(() => {
        makeComputerMove();
        setIsComputerThinking(false);
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [xIsNext, winner, gameMode, isReviewing]);
  
  // Make move function
  const handleClick = (i) => {
    // Don't allow moves if:
    // - there's already a winner
    // - the square is already filled
    // - it's computer's turn in vsComputer mode
    // - we're reviewing past moves
    if (
      winner || 
      board[i] || 
      (gameMode === 'vsComputer' && !xIsNext) || 
      isReviewing ||
      isComputerThinking
    ) {
      return;
    }
    
    const newBoard = board.slice();
    newBoard[i] = xIsNext ? 'X' : 'O';
    
    // Store game history
    const newHistory = gameHistory.slice(0, currentMove);
    newHistory.push(newBoard);
    setGameHistory(newHistory);
    setCurrentMove(newHistory.length);
    
    setBoard(newBoard);
    setXIsNext(!xIsNext);
  };
  
  // Computer AI move
  const makeComputerMove = () => {
    if (winner) return;
    
    let move;
    
    switch (difficulty) {
      case 'easy':
        move = makeRandomMove();
        break;
      case 'hard':
        move = makeBestMove();
        break;
      case 'medium':
      default:
        // 70% chance of best move, 30% chance of random move
        move = Math.random() < 0.7 ? makeBestMove() : makeRandomMove();
        break;
    }
    
    if (move !== null) {
      const newBoard = board.slice();
      newBoard[move] = 'O';
      
      // Store game history
      const newHistory = gameHistory.slice(0, currentMove);
      newHistory.push(newBoard);
      setGameHistory(newHistory);
      setCurrentMove(newHistory.length);
      
      setBoard(newBoard);
      setXIsNext(true);
    }
  };
  
  // Random move for easy difficulty
  const makeRandomMove = () => {
    const availableMoves = board.map((square, i) => square === null ? i : null).filter(i => i !== null);
    
    if (availableMoves.length === 0) return null;
    
    return availableMoves[Math.floor(Math.random() * availableMoves.length)];
  };
  
  // Minimax algorithm for hard difficulty
  const makeBestMove = () => {
    let bestScore = -Infinity;
    let bestMove = null;
    
    // Try each available square
    for (let i = 0; i < 9; i++) {
      if (board[i] === null) {
        // Make a move
        const newBoard = board.slice();
        newBoard[i] = 'O';
        
        // Evaluate move
        const score = minimax(newBoard, 0, false);
        
        // Undo move
        if (score > bestScore) {
          bestScore = score;
          bestMove = i;
        }
      }
    }
    
    return bestMove;
  };
  
  // Minimax helper function
  const minimax = (board, depth, isMaximizing) => {
    const result = calculateWinner(board);
    
    // Terminal states
    if (result) {
      if (result.winner === 'O') return 10 - depth; // Computer wins
      if (result.winner === 'X') return depth - 10; // Player wins
      return 0; // Draw
    }
    
    if (isMaximizing) {
      // Computer's turn (O)
      let bestScore = -Infinity;
      for (let i = 0; i < 9; i++) {
        if (board[i] === null) {
          const newBoard = board.slice();
          newBoard[i] = 'O';
          const score = minimax(newBoard, depth + 1, false);
          bestScore = Math.max(score, bestScore);
        }
      }
      return bestScore;
    } else {
      // Player's turn (X)
      let bestScore = Infinity;
      for (let i = 0; i < 9; i++) {
        if (board[i] === null) {
          const newBoard = board.slice();
          newBoard[i] = 'X';
          const score = minimax(newBoard, depth + 1, true);
          bestScore = Math.min(score, bestScore);
        }
      }
      return bestScore;
    }
  };
  
  // Reset game function
  const resetGame = () => {
    setBoard(Array(9).fill(null));
    setXIsNext(true);
    setWinner(null);
    setGameHistory([]);
    setCurrentMove(0);
    setIsReviewing(false);
  };
  
  // Handle time travel (game history)
  const jumpTo = (move) => {
    setCurrentMove(move);
    setBoard(move === 0 ? Array(9).fill(null) : gameHistory[move - 1]);
    setXIsNext(move % 2 === 0);
    setWinner(null);
    setIsReviewing(move < gameHistory.length);
  };
  
  // Render square component
  const Square = ({ value, onClick, highlight }) => {
    return (
      <button
        onClick={onClick}
        style={{
          width: '80px',
          height: '80px',
          fontSize: '32px',
          fontWeight: 'bold',
          cursor: 'pointer',
          backgroundColor: highlight ? 'rgba(70, 130, 220, 0.2)' : 'rgba(30, 30, 50, 0.6)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '8px',
          color: value === 'X' ? '#4caf50' : '#f44336',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
          boxShadow: highlight ? '0 0 10px rgba(70, 130, 220, 0.5)' : 'none',
        }}
        aria-label={`Square ${value || ''}`}
      >
        {value}
      </button>
    );
  };
  
  // Status message
  const getStatusMessage = () => {
    if (winner) {
      if (winner.winner === 'draw') {
        return "It's a draw!";
      } else {
        return `Player ${winner.winner} wins!`;
      }
    } else if (isComputerThinking) {
      return "Computer is thinking...";
    } else {
      return `Next player: ${xIsNext ? 'X' : 'O'}`;
    }
  };
  
  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      backgroundColor: 'rgba(20, 20, 35, 0.8)',
      backdropFilter: 'blur(20px)',
      color: 'white',
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>Tic Tac Toe</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <select
            value={gameMode}
            onChange={(e) => {
              setGameMode(e.target.value);
              resetGame();
            }}
            style={{
              backgroundColor: 'rgba(40, 40, 70, 0.6)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: '8px 12px',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            <option value="vsComputer">vs Computer</option>
            <option value="twoPlayer">Two Players</option>
          </select>
          
          {gameMode === 'vsComputer' && (
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              style={{
                backgroundColor: 'rgba(40, 40, 70, 0.6)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                padding: '8px 12px',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          )}
          
          <button
            onClick={resetGame}
            style={{
              backgroundColor: 'rgba(70, 130, 220, 0.8)',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <i className="fas fa-redo"></i>
            New Game
          </button>
        </div>
      </div>
      
      <div style={{ display: 'flex', gap: '20px', flex: 1 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Game status */}
          <div style={{ 
            fontSize: '18px', 
            marginBottom: '20px',
            padding: '10px 20px',
            backgroundColor: 'rgba(40, 40, 70, 0.6)',
            borderRadius: '8px',
            fontWeight: '500'
          }}>
            {getStatusMessage()}
          </div>
          
          {/* Game board */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridTemplateRows: 'repeat(3, 1fr)',
            gap: '10px',
            marginBottom: '20px'
          }}>
            {board.map((square, i) => (
              <Square
                key={i}
                value={square}
                onClick={() => handleClick(i)}
                highlight={winner && winner.line.includes(i)}
              />
            ))}
          </div>
          
          {/* Score board */}
          <div style={{ 
            display: 'flex', 
            gap: '15px', 
            marginTop: '10px',
            marginBottom: '20px'
          }}>
            <div style={{ 
              backgroundColor: 'rgba(76, 175, 80, 0.2)', 
              padding: '10px 20px', 
              borderRadius: '8px',
              textAlign: 'center',
              minWidth: '80px'
            }}>
              <div style={{ fontSize: '14px', marginBottom: '5px', color: 'rgba(255, 255, 255, 0.8)' }}>Player X</div>
              <div style={{ fontSize: '24px', fontWeight: '600', color: '#4caf50' }}>{score.x}</div>
            </div>
            <div style={{ 
              backgroundColor: 'rgba(120, 120, 140, 0.2)', 
              padding: '10px 20px', 
              borderRadius: '8px',
              textAlign: 'center',
              minWidth: '80px'
            }}>
              <div style={{ fontSize: '14px', marginBottom: '5px', color: 'rgba(255, 255, 255, 0.8)' }}>Ties</div>
              <div style={{ fontSize: '24px', fontWeight: '600' }}>{score.ties}</div>
            </div>
            <div style={{ 
              backgroundColor: 'rgba(244, 67, 54, 0.2)', 
              padding: '10px 20px', 
              borderRadius: '8px',
              textAlign: 'center',
              minWidth: '80px'
            }}>
              <div style={{ fontSize: '14px', marginBottom: '5px', color: 'rgba(255, 255, 255, 0.8)' }}>Player O</div>
              <div style={{ fontSize: '24px', fontWeight: '600', color: '#f44336' }}>{score.o}</div>
            </div>
          </div>
        </div>
        
        {/* Game history */}
        <div style={{ 
          backgroundColor: 'rgba(30, 30, 50, 0.6)',
          borderRadius: '8px',
          padding: '15px',
          width: '200px',
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '16px' }}>Game History</h3>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <button
              onClick={() => jumpTo(0)}
              style={{
                backgroundColor: currentMove === 0 ? 'rgba(70, 130, 220, 0.8)' : 'rgba(40, 40, 70, 0.6)',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '6px',
                marginBottom: '8px',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer'
              }}
            >
              Go to game start
            </button>
            
            {gameHistory.map((_, move) => (
              <button
                key={move + 1}
                onClick={() => jumpTo(move + 1)}
                style={{
                  backgroundColor: currentMove === move + 1 ? 'rgba(70, 130, 220, 0.8)' : 'rgba(40, 40, 70, 0.6)',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer'
                }}
              >
                Move #{move + 1} ({move % 2 === 0 ? 'X' : 'O'})
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicTacToe; 